import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SEO from '../../components/SEO';
import TestCard from '../../components/TestCard/TestCard';
import { testsData } from '../../data/testsData';
import './Tests.css';
import { exercises } from '../../data/exercises';
import ExerciseCard from '../../components/ExerciseCard/ExerciseCard';
import { postsData } from '../../data/postsData';
import PostCard from '../../components/PostCard/PostCard';

const Materials: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const isTestsView = location.pathname === '/materials/tests';
    const isExercisesView = location.pathname === '/materials/exercises';
    const isArticlesView = location.pathname === '/materials/articles';
    const isSelectionView = !isTestsView && !isExercisesView && !isArticlesView;

    const getSeoData = () => {
        if (isTestsView) {
            return {
                title: 'Психологические тесты | Психолог Ольга Гальперина',
                description: 'Профессиональные психологические тесты для оценки эмоционального состояния, уровня тревожности и депрессии.',
                canonical: '/materials/tests'
            };
        }
        if (isExercisesView) {
            return {
                title: 'Психологические упражнения | Психолог Ольга Гальперина',
                description: 'Практические упражнения и техники для саморегуляции, снятия стресса и заземления.',
                canonical: '/materials/exercises'
            };
        }
        if (isArticlesView) {
            return {
                title: 'Статьи по онкопсихологии | Психолог Ольга Гальперина',
                description: 'Статьи онкопсихолога о психологической поддержке при онкологическом диагнозе, тревоге, помощи близким и адаптации к лечению.',
                canonical: '/materials/articles',
                keywords: 'онкопсихология, онкопсихолог, статьи по психологии, психологическая помощь при раке'
            };
        }
        return {
            title: 'Полезные материалы | Психолог Ольга Гальперина',
            description: 'Психологические тесты, упражнения и статьи для самопознания и саморегуляции.',
            canonical: '/materials'
        };
    };

    const seoData = getSeoData();

    return (
        <div className="tests-page fade-in">
            <SEO
                title={seoData.title}
                description={seoData.description}
                canonical={seoData.canonical}
                keywords={'keywords' in seoData ? (seoData as any).keywords : undefined}
            />

            <div className="tests-container">
                <div className="page-header">Полезные материалы</div>

                {isSelectionView ? (
                    <div className="materials-selection-grid fade-in">
                        <div className="category-card" onClick={() => navigate('/materials/tests')}>
                            <h2 className="category-title">Психологические тесты</h2>
                            <p className="category-description">
                                Проффесиональные методики для оценки вашего эмоционального состояния,
                                уровня тревожности и депрессии.
                            </p>
                            <button className="category-button">Перейти к тестам</button>
                        </div>

                        <div className="category-card" onClick={() => navigate('/materials/exercises')}>
                            <h2 className="category-title">Упражнения</h2>
                            <p className="category-description">
                                Практические техники для снятия стресса, заземления и восстановления
                                внутреннего равновесия.
                            </p>
                            <button className="category-button">Перейти к упражнениям</button>
                        </div>

                        <div className="category-card" onClick={() => navigate('/materials/articles')}>
                            <h2 className="category-title">Статьи</h2>
                            <p className="category-description">
                                Полезные статьи о психологической поддержке при онкологии, способах
                                справляться с тревогой и помощи близким.
                            </p>
                            <button className="category-button">Перейти к статьям</button>
                        </div>
                    </div>
                ) : (
                    <>
                        <button className="back-to-categories" onClick={() => navigate('/materials')}>
                            ← Назад к категориям
                        </button>

                        {isTestsView && (
                            <section className="materials-section fade-in">
                                <h2 className="section-title">Психологические тесты</h2>
                                <div className="tests-disclaimer">
                                    <p>
                                        Наши психологические тесты помогут вам лучше понять ваше эмоциональное состояние.
                                        Обратите внимание, что результаты носят ознакомительный характер и не являются медицинским диагнозом.
                                    </p>
                                </div>
                                <div className="tests-grid">
                                    {testsData.map(test => (
                                        <TestCard key={test.id} {...test} />
                                    ))}
                                </div>
                            </section>
                        )}

                        {isExercisesView && (
                            <section className="materials-section fade-in">
                                <h2 className="section-title">Упражнения</h2>
                                <div className="tests-disclaimer">
                                    <p>
                                        Психологические упражнения — это инструменты самопомощи, которые помогут вам снизить уровень стресса,
                                        справиться с тревогой и улучшить эмоциональное состояние. Выполняйте их в удобном для вас темпе.
                                    </p>
                                </div>
                                <div className="tests-grid">
                                    {exercises.map(exercise => (
                                        <ExerciseCard key={exercise.id} exercise={exercise} />
                                    ))}
                                </div>
                            </section>
                        )}

                        {isArticlesView && (
                            <section className="materials-section fade-in">
                                <h2 className="section-title">Статьи</h2>
                                <div className="tests-disclaimer">
                                    <p>
                                        Статьи о психологической поддержке при онкологическом диагнозе — для пациентов и их близких.
                                    </p>
                                </div>
                                <div className="tests-grid">
                                    {postsData.map(post => (
                                        <PostCard key={post.id} {...post} />
                                    ))}
                                </div>
                            </section>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default Materials;
